import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchChildJourney } from "@/lib/api";
import {
  ArrowLeft, Compass, Loader2, Trophy, BookOpen, Activity,
  Stethoscope, GraduationCap, Music, Sparkles, Mail,
} from "lucide-react";
import PageErrorBoundary from "@/components/PageErrorBoundary";
import useTrackPage from "@/hooks/useTrackPage";

const CATEGORY_ICON = {
  School: BookOpen,
  Sports: Activity,
  Activity: Sparkles,
  Medical: Stethoscope,
  Camp: Music,
  Education: GraduationCap,
};

function JourneyInner() {
  const { childId } = useParams();
  useTrackPage("child_journey_open", { child_id: childId }, childId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchChildJourney(childId)
      .then(setData).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [childId]);

  const milestonesByYear = useMemo(() => {
    if (!data?.milestones) return [];
    const grp = {};
    data.milestones.forEach((m) => {
      const y = (m.occurred_at || "").slice(0, 4) || "—";
      (grp[y] ||= []).push(m);
    });
    return Object.entries(grp).sort((a, b) => b[0].localeCompare(a[0]));
  }, [data]);

  const maxActivityCount = useMemo(() => {
    if (!data?.activities_breakdown) return 0;
    return Math.max(0, ...data.activities_breakdown.map((a) => a.count || 0));
  }, [data]);

  if (loading) {
    return (
      <div className="fcc-card p-16 text-center text-[var(--fcc-muted)]">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        <p className="text-sm mt-3">Loading journey…</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="fcc-card p-12 text-center text-[var(--fcc-muted)]">
        <p>Couldn&apos;t load this child&apos;s journey.</p>
        <Link to="/app/children" className="text-sm text-[var(--fcc-green)] font-semibold mt-3 inline-block">
          ← Back to all children
        </Link>
      </div>
    );
  }

  const child = data.child;

  return (
    <div data-testid="child-journey-page" className="space-y-8">
      <Link
        to={`/app/children/${childId}`}
        data-testid="child-journey-back"
        className="inline-flex items-center gap-1.5 text-sm text-[var(--fcc-ink-soft)] hover:text-[var(--fcc-green)]"
      >
        <ArrowLeft className="w-4 h-4" /> Back to profile
      </Link>

      <header className="fcc-card-accent p-7 sm:p-9" data-testid="child-journey-header">
        <p className="label-overline flex items-center gap-2"><Compass className="w-3 h-3" /> Growth Journey</p>
        <h1 className="font-display text-4xl text-[var(--fcc-ink)] mt-2">{child.name}&apos;s journey</h1>
        <p className="text-sm text-[var(--fcc-ink-soft)] mt-2 max-w-2xl">
          A scrapbook view of milestones, monthly stories, and activity patterns over time.
        </p>
        <ul className="mt-6 flex flex-wrap gap-2">
          <span className="fcc-pill bg-white text-[var(--fcc-ink)]">{data.milestones.length} milestones</span>
          <span className="fcc-pill bg-white text-[var(--fcc-ink)]">{data.stories.length} monthly stories</span>
          <span className="fcc-pill bg-white text-[var(--fcc-ink)]">{data.activities_breakdown.length} categories</span>
        </ul>
      </header>

      <section data-testid="journey-activities-section" className="fcc-card p-6 sm:p-7">
        <header className="mb-4">
          <p className="label-overline">Activity breakdown</p>
          <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-1.5">What {child.name}&apos;s time looks like</h2>
        </header>
        {data.activities_breakdown.length === 0 ? (
          <p className="text-sm text-[var(--fcc-muted)]">No categorized activity yet.</p>
        ) : (
          <ul className="space-y-2.5">
            {data.activities_breakdown.map((a) => {
              const Icon = CATEGORY_ICON[a.category] || Sparkles;
              const pct = maxActivityCount > 0 ? Math.round((a.count / maxActivityCount) * 100) : 0;
              return (
                <li key={a.category} data-testid={`journey-activity-${a.category}`} className="flex items-center gap-3">
                  <span className="inline-flex w-8 h-8 rounded-lg bg-[var(--fcc-cream-2)] items-center justify-center shrink-0">
                    <Icon className="w-3.5 h-3.5 text-[var(--fcc-green)]" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="font-semibold text-sm text-[var(--fcc-ink)]">{a.category || "Other"}</span>
                      <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">{a.count} emails</span>
                    </div>
                    <div className="h-2 rounded-full bg-[var(--fcc-cream)] overflow-hidden">
                      <div className="h-full bg-[var(--fcc-green)]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section data-testid="journey-stories-section">
        <header className="mb-4">
          <p className="label-overline">Monthly stories</p>
          <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-1.5">{child.name}&apos;s recent chapters</h2>
        </header>
        {data.stories.length === 0 ? (
          <div className="fcc-card p-10 text-center text-sm text-[var(--fcc-muted)]">
            No monthly stories generated yet. Visit Family Story to create one.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {data.stories.map((s, idx) => {
              const monthLabel = new Date(s.year, (s.month || 1) - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
              return (
                <Link
                  key={idx}
                  to={`/app/family-story?y=${s.year}&m=${s.month}&child_id=${childId}`}
                  data-testid={`journey-story-${s.year}-${s.month}`}
                  className="fcc-card p-5 hover:bg-[var(--fcc-cream-2)] transition"
                >
                  <p className="label-overline">{monthLabel}</p>
                  <p className="text-sm text-[var(--fcc-ink-soft)] leading-relaxed mt-2 line-clamp-4">
                    {s.narrative || "Story unavailable."}
                  </p>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      <section data-testid="journey-milestones-section">
        <header className="mb-4">
          <p className="label-overline">Milestones · by year</p>
          <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-1.5">{child.name}&apos;s wins, kept forever</h2>
        </header>
        {milestonesByYear.length === 0 ? (
          <div className="fcc-card p-10 text-center text-sm text-[var(--fcc-muted)]">
            No milestones yet. They&apos;ll appear here automatically as KidHQ detects them.
          </div>
        ) : (
          <div className="space-y-5">
            {milestonesByYear.map(([year, items]) => (
              <article key={year} className="fcc-card p-5" data-testid={`journey-year-${year}`}>
                <header className="flex items-baseline justify-between mb-3">
                  <h3 className="font-display text-2xl text-[var(--fcc-ink)]">{year}</h3>
                  <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">{items.length} milestones</span>
                </header>
                <ol className="relative border-l-2 border-[var(--fcc-border)] pl-5 space-y-3">
                  {items.map((m, idx) => (
                    <li key={idx} data-testid="journey-milestone" className="relative">
                      <span className="absolute -left-[27px] top-1 w-3 h-3 rounded-full border-2 border-white bg-[var(--fcc-green)]" />
                      <p className="font-semibold text-sm text-[var(--fcc-ink)]">{m.label}</p>
                      <p className="text-xs text-[var(--fcc-muted)] mt-0.5">
                        {(m.occurred_at || "").slice(0, 10)}
                        {m.kind ? ` · ${m.kind}` : ""}
                      </p>
                    </li>
                  ))}
                </ol>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default function ChildJourneyPage() {
  return (
    <PageErrorBoundary scope="child-journey">
      <JourneyInner />
    </PageErrorBoundary>
  );
}
